import React, { useState, useMemo, useEffect } from 'react';
import { MdArrowBack, MdCameraAlt, MdSwapHoriz, MdSend, MdSearch, MdClose } from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp, maskAmount } from '../contexts/AppContext';
import { denormalizeTransactions } from '../utils/denormalizeTransactions';
import accountsData from '../data/accounts.json';
import transactionsData from '../data/transactions.json';

const bankingData = accountsData.bankingData;
const investingData = accountsData.investingData;
const accountTransactions = transactionsData.accountTransactions;

const AccountDetailsScreen = ({ onNavigate, onBack, accountId }) => {
  const { loadedProfileData } = useProfile();
  const { hideBalances, simDate } = useApp();
  const [visibleMonths, setVisibleMonths] = useState(3);
  const [tab, setTab] = useState('activity');
  const [searchQuery, setSearchQuery] = useState('');

  // When sim date advances, show all months available in the filtered window
  useEffect(() => { setVisibleMonths(simDate ? 999 : 3); }, [simDate]);

  // Build account + transactions from loaded profile if present
  const profileData = useMemo(() => {
    const profile = loadedProfileData?.profile;
    if (!profile?.accounts) return null;
    const acct = profile.accounts[accountId];
    if (!acct) return null;

    const allTxns = denormalizeTransactions(acct.transactions)
      .filter(t => t.date)
      .sort((a, b) => b.date.localeCompare(a.date));

    // In sim mode: only show transactions up to simDate
    const flatTxns = simDate ? allTxns.filter(t => t.date <= simDate) : allTxns;
    const allMonths = [...new Set(flatTxns.map(t => t.date.slice(0, 7)))].sort().reverse();

    const name = acct.account_type || acct.name || acct.type || accountId;
    const rawBalance = acct.current_balance ?? acct.current_balance_owing ?? acct.current_value ?? acct.current_market_value ?? acct.balance;

    // Roll back balance to simDate using balance_after (checking/savings) or
    // subtracting future charges (credit cards). Investments keep live value.
    let balance = rawBalance;
    if (simDate && acct.transactions) {
      const chronological = [...allTxns].sort((a, b) => a.date.localeCompare(b.date));
      const isInvestment = acct.current_value !== undefined || acct.current_market_value !== undefined;
      if (!isInvestment && chronological.length) {
        const last = chronological[chronological.length - 1];
        if ('balance_after' in last && Math.abs((last.balance_after ?? NaN) - rawBalance) < 1) {
          const past = chronological.filter(t => t.date <= simDate);
          balance = past.length ? past[past.length - 1].balance_after : 0;
        } else if (acct.current_balance_owing !== undefined) {
          const futureSum = chronological.filter(t => t.date > simDate).reduce((s, t) => s + (t.amount ?? 0), 0);
          balance = rawBalance + futureSum;
        }
      }
    }

    // Hero shows whole dollars (no cents) per design — keeps the headline legible.
    const balanceFmt = `$${Math.round(Math.abs(balance ?? 0)).toLocaleString('en-CA')}`;

    return { name, balance: balanceFmt, flatTxns, allMonths };
  }, [loadedProfileData, accountId, simDate]);

  const groupedTxns = useMemo(() => {
    if (!profileData) return null;
    const { flatTxns, allMonths } = profileData;
    const q = searchQuery.trim().toLowerCase();
    // When a search query is active, we ignore the visibleMonths window so
    // the user can find matches across their full history. With no query,
    // we limit to recent months and let "Load older" extend the window.
    const visible = q
      ? flatTxns.filter(t =>
          (t.description || '').toLowerCase().includes(q) ||
          (t.category    || '').toLowerCase().includes(q))
      : flatTxns.filter(t => new Set(allMonths.slice(0, visibleMonths)).has(t.date.slice(0, 7)));

    const byDate = {};
    visible.forEach(t => {
      const key = t.date;
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(t);
    });

    const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return Object.entries(byDate)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateStr, txns]) => {
        const [, m, d] = dateStr.split('-');
        const label = `${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
        return {
          dateGroup: label,
          items: txns.map(t => ({
            id: t.id || t.date + t.description,
            merchant: t.description,
            category: t.category,
            amount: (t.amount < 0 ? '-' : '+') + '$' + Math.abs(t.amount).toLocaleString('en-CA', { minimumFractionDigits: 2 }),
            isPositive: t.amount > 0,
          })),
        };
      });
  }, [profileData, visibleMonths, searchQuery]);

  // Count of matched transactions when a query is active — used for the
  // "N results" label below the search input.
  const matchedCount = useMemo(() => {
    if (!searchQuery.trim() || !groupedTxns) return null;
    return groupedTxns.reduce((s, g) => s + g.items.length, 0);
  }, [groupedTxns, searchQuery]);

  const allAccounts = [...bankingData, ...investingData];
  const defaultAccount = allAccounts.find(acc => acc.id === accountId);
  const defaultTransactions = accountTransactions[accountId] || [];

  const account = profileData ? { name: profileData.name, balance: profileData.balance } : defaultAccount;
  const transactions = groupedTxns ?? defaultTransactions;

  if (!account) {
    return (
      <div className="screen bh-flow">
        <header className="bh-flow-hero">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack || (() => onNavigate('accounts'))} aria-label="Back to accounts">
              <MdArrowBack />
            </button>
            <h1 className="bh-flow-title">Account Details</h1>
          </div>
        </header>
        <div className="bh-flow-body">
          <div className="bh-form-card" style={{ alignItems: 'center' }}>
            <p style={{ color: 'var(--bh-gray)', font: 'var(--bh-body)' }}>Account not found</p>
          </div>
        </div>
      </div>
    );
  }

  const hasMore = profileData && visibleMonths < profileData.allMonths.length;

  return (
    // Single scroll container — hero, balance, actions, tabs, and the
    // transaction list all scroll together. (The bh-flow split-pane layout
    // was preventing scroll past the action row on this screen.)
    <div className="screen account-details-screen">
      <div className="scroll-content account-details-scroll">
        <header className="bh-flow-hero tall">
          <div className="bh-flow-hero-row">
            <div className="bh-flow-hero-left">
              <button className="bh-flow-back" onClick={onBack || (() => onNavigate('accounts'))} aria-label="Back to accounts">
                <MdArrowBack />
              </button>
              <h1 className="bh-flow-title">Account</h1>
            </div>
          </div>

          <div className="bh-balance-card">
            <p className="bh-balance-card-label">
              {simDate ? `Balance as of ${simDate}` : 'Current Balance'}
            </p>
            <p className="bh-balance-card-amount">{maskAmount(account.balance, hideBalances)}</p>
            <p style={{ margin: 'var(--space-2xs) 0 0', font: 'var(--bh-body-sm)', opacity: 0.8 }}>
              {account.name}
              {simDate && <span style={{ marginLeft: 8, color: '#f59e0b', fontWeight: 600 }}>SIM</span>}
            </p>
          </div>
        </header>

        <div className="account-details-content">
          {/* Quick actions */}
          <div className="bh-form-card">
            <div className="bh-action-carousel">
              <button className="bh-action-card" onClick={() => onNavigate('emailTransfer')}>
                <div className="bh-action-card-icon"><MdSend /></div>
                <span>Send</span>
              </button>
              <button className="bh-action-card">
                <div className="bh-action-card-icon"><MdSwapHoriz /></div>
                <span>Transfer</span>
              </button>
              <button className="bh-action-card">
                <div className="bh-action-card-icon"><MdCameraAlt /></div>
                <span>Deposit</span>
              </button>
            </div>
          </div>

          {/* Activity / Details tabs */}
        <div className="bh-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'activity'}
            className={`bh-tab${tab === 'activity' ? ' active' : ''}`}
            onClick={() => setTab('activity')}
          >
            Activity
          </button>
          <button
            role="tab"
            aria-selected={tab === 'details'}
            className={`bh-tab${tab === 'details' ? ' active' : ''}`}
            onClick={() => setTab('details')}
          >
            Details
          </button>
        </div>

        {tab === 'activity' && (
          <>
            {/* Search input — only shown when there is a profile feeding
                the activity list (default mock data isn't keyed by
                category, so the search wouldn't be useful there). */}
            {profileData && (
              <div className="bh-form-card" style={{ padding: 'var(--space-2xs) var(--space-sm)', gap: 'var(--space-3xs)' }}>
                <div style={{ position: 'relative' }}>
                  <MdSearch size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--bh-gray)', pointerEvents: 'none' }} />
                  <input
                    className="bh-field-input"
                    type="search"
                    placeholder="Search description or category"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ paddingLeft: 36, paddingRight: searchQuery ? 36 : 12 }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--bh-gray)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 6 }}
                    >
                      <MdClose size={18} />
                    </button>
                  )}
                </div>
                {matchedCount != null && (
                  <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                    {matchedCount} match{matchedCount === 1 ? '' : 'es'} across full history
                  </span>
                )}
              </div>
            )}

            {transactions.length > 0 ? (
              <>
                <div className="bh-tx-list" role="tabpanel">
                  {transactions.map((group, index) => (
                    <React.Fragment key={index}>
                      <div className="bh-tx-group-title">{group.dateGroup}</div>
                      {group.items.map(item => (
                        <div key={item.id} className="bh-tx-row">
                          <div className="bh-tx-row-info">
                            <span className="bh-tx-merchant">{item.merchant}</span>
                            {item.category && (
                              <span className="bh-tx-date" style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                                {item.category}
                              </span>
                            )}
                          </div>
                          <span className={`bh-tx-amount${item.isPositive ? ' positive' : ''}`}>
                            {item.amount}
                          </span>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                {hasMore && !searchQuery.trim() && (
                  <button className="bh-btn-outline-dark" onClick={() => setVisibleMonths(m => m + 1)}>
                    Load older transactions ({profileData.allMonths.length - visibleMonths} more month{profileData.allMonths.length - visibleMonths !== 1 ? 's' : ''})
                  </button>
                )}
              </>
            ) : (
              <div className="bh-form-card" style={{ alignItems: 'center' }}>
                <p style={{ margin: 0, color: 'var(--bh-gray)', font: 'var(--bh-body-sm)' }}>
                  {searchQuery.trim() ? `No transactions match "${searchQuery}"` : 'No transactions found'}
                </p>
              </div>
            )}
          </>
        )}

        {tab === 'details' && (
          <div className="bh-form-card" role="tabpanel">
            <h3 style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>Account Details</h3>
            <div className="bh-review-rows">
              <div className="bh-review-row">
                <span className="bh-review-row-label">Account</span>
                <span className="bh-review-row-value">{account.name}</span>
              </div>
              <div className="bh-review-row">
                <span className="bh-review-row-label">Balance</span>
                <span className="bh-review-row-value">{account.balance}</span>
              </div>
              {profileData?.flatTxns?.length != null && (
                <div className="bh-review-row">
                  <span className="bh-review-row-label">Transactions on file</span>
                  <span className="bh-review-row-value">{profileData.flatTxns.length}</span>
                </div>
              )}
            </div>
          </div>
        )}
        </div>{/* /.account-details-content */}
      </div>{/* /.scroll-content */}
    </div>
  );
};

export default AccountDetailsScreen;
